import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ui/icons';
import { Button, IconButton } from '@ui/button';
import { ResponsiveImage } from '@web/molecules';
import { useReporter } from '../../reporter';
import {
  userViewsOnboardingIntro,
  userContinuesFromIntro,
  onboardingBackArrowClicked,
} from '../events';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import {
  StyledOnboardingIntro,
  MobileImageContainer,
  DesktopImageContainer,
  ImageContainer,
  Headline,
  ButtonContainer,
  BodyWrapper,
  Body,
  MobileHeader,
  DesktopHeader,
  PopUpBar,
} from './styles.js';

export const OnboardingIntro = ({
  goBack,
  onContinue,
  mobileImage,
  desktopImage,
  headline,
  ctaButtonText = 'Start the quiz',
  body,
  ollieLogo,
  fontColor = 'text.primary',
  popUpText,
  popUpFontColor = 'text.lightest',
  popUpBgColor = 'status.successPrimary',
  popUpAlignment = 'start',
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const { cart } = useShopifyCartContext();

  const reporter = useReporter();
  const viewPageTagFired = useRef(false);
  useEffect(() => {
    if (!viewPageTagFired.current) {
      reporter.tag(userViewsOnboardingIntro());
      viewPageTagFired.current = true;
    }
  }, [reporter]);

  useEffect(() => {
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 5000);
  }, []);
  const handleSubmit = () => {
    reporter.tag(userContinuesFromIntro());
    onContinue();
  };

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('onboarding_intro', cart?.customer),
    );
    goBack();
  };

  return (
    <>
      <StyledOnboardingIntro>
        <MobileHeader>
          <ResponsiveImage priority={true} {...ollieLogo} />
          <Headline $fontColor={fontColor}>{headline}</Headline>
        </MobileHeader>
        <ButtonContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            data-testid="onboarding-intro-back"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            type="button"
            variant="primary"
          />
        </ButtonContainer>
        <ImageContainer>
          <MobileImageContainer>
            <ResponsiveImage priority={true} {...mobileImage} width={400} />
          </MobileImageContainer>
          <DesktopImageContainer>
            <ResponsiveImage
              priority={true}
              {...desktopImage}
              height={362}
              width={406}
            />
          </DesktopImageContainer>
        </ImageContainer>
        <BodyWrapper $minHeight="0">
          {showPopup && popUpText && (
            <PopUpBar
              $fontColor={popUpFontColor}
              $bgColor={popUpBgColor}
              $alignment={popUpAlignment}
              showPopup={showPopup}
            >
              <Icon
                iconName="checkmark"
                alt="checkmark"
                color={popUpFontColor}
                size="17"
                id={`check`}
              />
              {popUpText}
            </PopUpBar>
          )}
          <DesktopHeader>
            <ResponsiveImage priority={true} {...ollieLogo} />
            <Headline $fontColor={fontColor}>{headline}</Headline>
          </DesktopHeader>
          <Body $fontColor={fontColor}>{body}</Body>
          <Button
            type="submit"
            aria-label={ctaButtonText}
            data-testid="onboarding-start-quiz"
            variant="primary"
            horizontalPadding={32}
            onClick={handleSubmit}
          >
            {ctaButtonText}
          </Button>
        </BodyWrapper>
      </StyledOnboardingIntro>
    </>
  );
};
OnboardingIntro.displayName = 'OnboardingIntro';
OnboardingIntro.propTypes = {
  ctaButtonText: PropTypes.string,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
  headline: PropTypes.string.isRequired,
  goBack: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  body: PropTypes.string,
  ollieLogo: PropTypes.object,
  popUpText: PropTypes.string,
  popUpBgColor: PropTypes.string,
  popUpFontColor: PropTypes.string,
  popUpAlignment: PropTypes.oneOf(['start', 'center', 'end']),
};
